/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
// src/common/MapComponent.js
import React, { useEffect, useState, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import PropTypes from "prop-types";
import { getRoute } from "../utils/route";
import { Spin } from "antd";
import "leaflet/dist/leaflet.css";
import { useCookieConsent } from "../CookieConsentContext";
import driverIconImage from "../Assets/driver-icon.png";
import originIconImage from "../Assets/origin-icon.png";
import destinationIconImage from "../Assets/destination-icon.png";

// Configuración de iconos por defecto
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const originIcon = new L.Icon({
  iconUrl: originIconImage,
  iconSize: [30, 50],
  iconAnchor: [15, 50],
  popupAnchor: [0, -50],
});

const destinationIcon = new L.Icon({
  iconUrl: destinationIconImage,
  iconSize: [30, 50],
  iconAnchor: [15, 50],
  popupAnchor: [0, -50],
});

const driverIcon = new L.Icon({
  iconUrl: driverIconImage,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -41],
});

const MapComponent = ({ origin, destination, driverLocation }) => {
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Para saber si el usuario aceptó cookies (Google vs OSM)
  const { cookiesAccepted } = useCookieConsent();

  /**
   * Bounds y centro para que el mapa se ajuste a las coordenadas disponibles
   * Leaflet usa [lat, lon]
   */
  const { bounds, center } = useMemo(() => {
    const validOrigin = origin && typeof origin.latitude === "number" && typeof origin.longitude === "number";
    const validDestination = destination && typeof destination.latitude === "number" && typeof destination.longitude === "number";
    const validDriver = driverLocation && typeof driverLocation.latitude === "number" && typeof driverLocation.longitude === "number";

    // Crear array de todas las posiciones válidas
    const positions = [];
    if (validOrigin) positions.push([origin.latitude, origin.longitude]);
    if (validDestination) positions.push([destination.latitude, destination.longitude]);
    if (validDriver) positions.push([driverLocation.latitude, driverLocation.longitude]);

    if (positions.length >= 2) {
      return {
        bounds: L.latLngBounds(positions),
        center: null
      };
    } else if (positions.length === 1) {
      return {
        bounds: null,
        center: positions[0]
      };
    }

    // Default center (Bogotá)
    return {
      bounds: null,
      center: [4.7110, -74.0721]
    };
  }, [origin, destination, driverLocation]);

  // Efecto para obtener la ruta de OpenRouteService
  useEffect(() => {
    const validOrigin = origin && typeof origin.latitude === "number" && typeof origin.longitude === "number";
    const validDestination = destination && typeof destination.latitude === "number" && typeof destination.longitude === "number";
    
    if (!validOrigin || !validDestination) {
      setRouteCoordinates([]);
      setLoading(false);
      return;
    }

    const fetchRoute = async () => {
      setLoading(true);
      try {
        const routeData = await getRoute(origin, destination);
        if (routeData && routeData.features?.length > 0) {
          const coords = routeData.features[0].geometry.coordinates.map(
            (coord) => [coord[1], coord[0]]
          );
          setRouteCoordinates(coords);
        } else {
          setRouteCoordinates([]);
        }
      } catch (error) {
        console.error("Error al obtener la ruta:", error);
        setRouteCoordinates([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoute();
  }, [origin, destination]);

  return (
    <>
      {loading ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 999,
          }}
        >
          <Spin />
        </div>
      ) : (
        <MapContainer
          style={{ height: "100%", width: "100%" }}
          bounds={bounds}
          center={center}
          zoom={bounds ? undefined : 13}
          scrollWheelZoom={true}
        >
          {cookiesAccepted ? (
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
              maxZoom={20}
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
              attribution='Map data &copy; <a href="https://www.google.com/maps">Google Maps</a>'
            />
          ) : (
            <TileLayer
              attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}

          {/* Marcador de Origen */}
          {origin && typeof origin.latitude === "number" && typeof origin.longitude === "number" && (
            <Marker
              position={[origin.latitude, origin.longitude]}
              icon={originIcon}
            >
              <Popup>📍 Origen</Popup>
            </Marker>
          )}

          {/* Marcador de Destino */}
          {destination && typeof destination.latitude === "number" && typeof destination.longitude === "number" && (
            <Marker
              position={[destination.latitude, destination.longitude]}
              icon={destinationIcon}
            >
              <Popup>📍 Destino</Popup>
            </Marker>
          )}

          {/* Marcador del Conductor (si existe) */}
          {driverLocation && (
            <Marker
              position={[driverLocation.latitude, driverLocation.longitude]}
              icon={driverIcon}
            >
              <Popup>🚗 Conductor</Popup>
            </Marker>
          )}

          {/* Dibuja la polyline si tenemos coords */}
          {routeCoordinates.length > 0 && (
            <Polyline positions={routeCoordinates} color="blue" />
          )}
        </MapContainer>
      )}
    </>
  );
};

MapComponent.propTypes = {
  origin: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  destination: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  driverLocation: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
};

export default MapComponent;
